// UserContext.js - Vamos adicionar mais logs para debug
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser deve ser usado dentro de um UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const firestoreUnsubscribeRef = useRef(null);

  useEffect(() => {
    console.log('🔄 UserProvider iniciando...');
    
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      console.log('🔥 Auth state changed:', user?.email);
      setLoading(true);
      
      // Limpar observação anterior do Firestore se existir
      if (firestoreUnsubscribeRef.current) {
        firestoreUnsubscribeRef.current();
        firestoreUnsubscribeRef.current = null;
      }

      if (user) {
        console.log('👤 Usuário autenticado:', user.email);
        setUser(user);

        // Garantir que exista documento de usuário / atualizar lastLogin
        const userDocRef = doc(db, 'users', user.uid);
        (async () => {
          try {
            const snap = await getDoc(userDocRef);
            if (!snap.exists()) {
              console.log('📄 Criando documento Firestore para novo usuário');
              await setDoc(userDocRef, {
                uid: user.uid,
                name: user.displayName || '',
                email: user.email,
                emailVerified: user.emailVerified || false,
                platform: user.providerData[0]?.providerId || 'unknown',
                createdAt: new Date(),
                lastLogin: new Date(),
                plan: 'free',
                active: true
              });
            } else {
              console.log('📝 Atualizando lastLogin do usuário');
              await updateDoc(userDocRef, { lastLogin: new Date() });
            }
          } catch (e) {
            console.error('Erro ao criar/atualizar doc do usuário:', e);
          }
        })();
        
        // 🔥 OBSERVAÇÃO EM TEMPO REAL do documento do usuário no Firestore
        const unsubscribe = onSnapshot(userDocRef, 
          (docSnap) => {
            if (docSnap.exists()) {
              const userDataFromFirestore = docSnap.data();
              console.log('📡 Dados do Firestore carregados:', {
                email: userDataFromFirestore.email,
                plan: userDataFromFirestore.plan
              });
              setUserData(userDataFromFirestore);
            } else {
              console.log('❌ Documento do usuário não encontrado no Firestore');
              setUserData(null);
            }
            setLoading(false);
          }, 
          (error) => {
            console.error('❌ Erro ao observar dados do usuário:', error);
            setUserData(null);
            setLoading(false);
          }
        );

        firestoreUnsubscribeRef.current = unsubscribe;
      } else {
        console.log('🚫 Nenhum usuário autenticado');
        setUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      console.log('🧹 UserProvider limpando...');
      unsubscribeAuth();
      if (firestoreUnsubscribeRef.current) {
        firestoreUnsubscribeRef.current();
      }
    };
  }, []);

  const logout = async () => {
    try {
      console.log('🚪 Iniciando logout...');
      
      if (firestoreUnsubscribeRef.current) {
        firestoreUnsubscribeRef.current();
        firestoreUnsubscribeRef.current = null;
      }
      
      setUser(null);
      setUserData(null);
      
      await signOut(auth);
      console.log('✅ Logout do Firebase realizado');
      
      localStorage.clear();
      sessionStorage.clear();
      
      return true;
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      setUser(null);
      setUserData(null);
      localStorage.clear();
      sessionStorage.clear();
      return false;
    }
  };

  const value = {
    user,
    userData,
    loading,
    setUserData,
    logout
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};